import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IonInput, NavController, ToastController } from '@ionic/angular';
import { Auth } from 'aws-amplify';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';

@Component({
  selector: 'app-forgot-password-submit',
  templateUrl: './forgot-password-submit.component.html',
  styleUrls: ['./forgot-password-submit.component.scss'],
})
export class ForgotPasswordSubmitComponent implements OnInit {

  public error: any;
  public invalidCodeError: any;
  public errorMessage: string;
  public form: UntypedFormGroup;
  public submitPressed: boolean = false;

  private showPassword: boolean = false;
  private username: string;

  @ViewChild('passwordInput')
  private passwordInput: IonInput;

  @ViewChild('passwordVerificationInput')
  private passwordVerificationInput: IonInput;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly navCtrl: NavController,
    private readonly route: ActivatedRoute,
    private readonly toastCtrl: ToastController,
  ) { }

  verifyPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const passwordFieldName = 'password';
    const passwordVerificationFieldName = 'passwordVerification';
    const password = control.get(passwordFieldName);
    const passwordVerification = control.get(passwordVerificationFieldName);
    if (password && passwordVerification && password.value !== passwordVerification.value) {
      const invalidObj = { passwordVerification: false };
      control.get(passwordVerificationFieldName).setErrors(invalidObj);
      return invalidObj;
    } else {
      control.get(passwordVerificationFieldName).setErrors(null);
    }
  };

  ngOnInit() {
    this.username = this.route.snapshot.queryParamMap.get('username');
    this.form = this.formBuilder.group({
      verificationCode: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(6)])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(128)])],
      passwordVerification: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(128)])]
    }, { validators: this.verifyPasswordValidator });
  }

  async resendConfirmationCode() {
    await this.navCtrl.navigateBack('account/forgot-password');
  }

  async onCodeChanged($event: string) {
    this.form.controls.verificationCode.setValue($event);
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
    this.passwordInput.type = this.showPassword ? 'text' : 'password';
    this.passwordVerificationInput.type = this.showPassword ? 'text' : 'password';
  }

  async onSubmit() {
    this.submitPressed = true;
    if (this.form.invalid) {
      this.errorMessage = 'All form fields are required.';
      return;
    }

    try {
      await Auth.forgotPasswordSubmit(this.username, this.form.value.verificationCode, this.form.value.password);
      const toast = await this.toastCtrl.create({
        cssClass: 'success-toast',
        message: '<img src="assets/icons/completed-check.svg"></img> Password successfully changed!',
        duration: 1500,
        position: 'bottom',
      });
      await toast.present();
      await this.navCtrl.navigateRoot(AppRoutingUtil.ACCOUNT_SIGNIN);
    } catch (error) {
      // If we're getting a verification code mismatch error we want to customize our message to include a resend code hyperlink
      // All other cases such as password too short, password doesn't have special characters etc can just be printed directly to user
      if (error.message.includes("verification")) {
        this.invalidCodeError = error.message;
        this.error = null;
      } else {
        this.error = error.message;
        this.invalidCodeError = null;
      }
      const toast = await this.toastCtrl.create({
        cssClass: 'error-toast',
        message: '<img src="assets/icons/alert-triangle.svg"></img> ' + error.message,
        duration: 10000,
        position: 'bottom',
      });
      await toast.present();
    }
  }
}
